import { useEffect, useMemo, useState } from "react";
import { message, Upload } from "antd";

import { IUploadDocument } from "../../Components/UploadDocument/IUploadDocument";
import { UploadDocumentAPI } from "../../Api/UploadDocumentAPI";

export const DEFAULT_MAX_FILE_SIZE_MB = 30;
const ALLOWED_FILE_TYPES = ["image/png", "image/jpeg", "application/pdf", "application/vnd.openxmlformats-officedocument.wordprocessingml.document"];
const ALLOWED_EXTENSIONS = [".pdf", ".jpg", ".jpeg", ".png"];

const useUploadDocument = ({
	baseURL,
	clientId,
	translations,
	ocrFields,
	shouldRunOcr,
	uploadedFile,
	allowMultipleDocuments = false,
	maxDocuments,
	maxFileSizeMB = DEFAULT_MAX_FILE_SIZE_MB,
	fileName,
	allowCustomFileName = false,
	customFileName,
	extraFormatsAllowed,
	updateOCRField,
	updateCustomFileName,
	changeIsLoagindOcr,
	updateFileUrl
}: IUploadDocument) => {
	const api = useMemo(() => UploadDocumentAPI({ baseURL, clientId }), [baseURL, clientId]);
	const [isOcrLoading, setIsOcrLoading] = useState<boolean>(false);
	const [token, setToken] = useState<string>("");
	const [fileUrl, setFileUrl] = useState<string>("");
	const [pendingFiles, setPendingFiles] = useState<File[]>([]);
	const [internalCustomFileName, setInternalCustomFileName] = useState<string>(customFileName ?? "");

	useEffect(() => {
		setFileUrl(uploadedFile ?? "");
	}, [uploadedFile]);

	useEffect(() => {
		setInternalCustomFileName(customFileName ?? "");
	}, [customFileName]);

	useEffect(() => {
		const getToken = async () => {
			const response = await api.getOcrToken();
			if (response?.data) {
				setToken(response.data);
			}
		};
		if (shouldRunOcr) getToken();
	}, [api, shouldRunOcr]);

	const isSingleFileCustomNameMode = allowCustomFileName && !allowMultipleDocuments;
	const shouldQueueFilesBeforeUpload = allowMultipleDocuments || allowCustomFileName;
	const resolvedFileName = allowCustomFileName
		? (customFileName ?? internalCustomFileName)
		: fileName;

	const canUploadPendingFiles = useMemo(
		() => allowMultipleDocuments && pendingFiles.length > 0,
		[allowMultipleDocuments, pendingFiles.length]
	);

	const shouldDisableFileDialog = (
		(isSingleFileCustomNameMode && pendingFiles.length > 0) ||
		(allowMultipleDocuments && pendingFiles.length > 0)
	);
	const shouldShowCustomFileNameInput = allowCustomFileName && pendingFiles.length > 0;
	const displayCustomFileName = customFileName ?? internalCustomFileName;

	const updateFieldsFromOCR = (
		ocrObject: any
	) => {
		ocrFields?.forEach((fieldName: string) => {
			const pascalCaseField = fieldName.charAt(0).toUpperCase() + fieldName.slice(1);
			if (ocrObject[pascalCaseField]) {
				const value = ocrObject[pascalCaseField];
				updateOCRField && updateOCRField(value, fieldName);
			}
		});
	};

	const validateFile = (file: File) => {
		const extension = `.${file.name.split(".").pop()?.toLowerCase() ?? ""}`;
		const hasValidType = ALLOWED_FILE_TYPES.includes(file.type);
		const allowedExtensions = [...ALLOWED_EXTENSIONS, ...(extraFormatsAllowed ? extraFormatsAllowed.split(",").map(ext => ext.trim().toLowerCase()) : [])];
		const hasValidExtension = allowedExtensions.includes(extension);

		if (file.size > maxFileSizeMB * 1024 * 1024) {
			return `File size exceeds the limit of ${maxFileSizeMB}MB`;
		}

		if (!hasValidType && !hasValidExtension) {
			return `Invalid file format. Only ${allowedExtensions.join(", ")} are allowed.`;
		}

		return null;
	};

	const handleCustomFileNameChange = (value: string) => {
		setInternalCustomFileName(value);
		updateCustomFileName && updateCustomFileName(value);
	};

	const processUploadedFile = async (
		uploadedFileUrl: string,
		shouldOcr: boolean
	) => {
		setFileUrl(uploadedFileUrl);
		updateFileUrl(uploadedFileUrl);

		if (!shouldOcr) {
			return;
		}

		const ocrResponse = await api.extractDataFromDocument(
			token,
			uploadedFileUrl
		);

		if (ocrResponse) {
			const ocrData = ocrResponse[0]?.cleanedData;
			if (ocrData) {
				updateFieldsFromOCR(ocrData);
			}
		}
	};

	const uploadFiles = async (
		files: File[],
		runOcr: boolean,
		mergeFiles = false
	) => {
		try {
			if (!files.length) {
				message.error("Please select at least one file.");
				return false;
			}

			for (const file of files) {
				const validationError = validateFile(file);
				if (validationError) {
					message.error(validationError);
					return false;
				}
			}

			if (runOcr) {
				setIsOcrLoading(true);
				changeIsLoagindOcr && changeIsLoagindOcr(true);
			}

			const uploadResponse = mergeFiles
				? await api.uploadMergedFiles(files, resolvedFileName)
				: await api.uploadFile(files[0], resolvedFileName);

			if (!uploadResponse?.data) {
				message.error("Error uploading file");
				return false;
			}

			await processUploadedFile(uploadResponse.data, runOcr);
			message.success(
				mergeFiles
					? "Files merged and uploaded successfully"
					: "File uploaded successfully"
			);
			return true;
		} catch (error) {
			console.error("Error uploading or processing file", error);
			message.error("Error uploading or processing file");
			return false;
		} finally {
			setIsOcrLoading(false);
			changeIsLoagindOcr && changeIsLoagindOcr(false);
		}
	};

	const handleBeforeUpload = async (
		file: File,
		runOcr: boolean
	) => {
		await uploadFiles([file], runOcr);
		return false;
	};

	const handleUploadPendingFiles = async (
		files: File[],
		runOcr: boolean
	) => uploadFiles(files, runOcr, files.length > 1);

	const handleQueueFile = (file: File) => {
		const validationError = validateFile(file);
		if (validationError) {
			message.error(validationError);
			return Upload.LIST_IGNORE;
		}

		setPendingFiles((currentFiles) => {
			if (!allowMultipleDocuments) {
				return [file];
			}

			const alreadyExists = currentFiles.some((currentFile) => (
				currentFile.name === file.name &&
				currentFile.size === file.size &&
				currentFile.lastModified === file.lastModified
			));

			if (alreadyExists) {
				return currentFiles;
			}

			if (maxDocuments && currentFiles.length >= maxDocuments) {
				message.error(`You can only select up to ${maxDocuments} documents.`);
				return currentFiles;
			}

			return [...currentFiles, file];
		});

		if (allowCustomFileName) {
			const defaultFileName = file.name.includes(".")
				? file.name.substring(0, file.name.lastIndexOf("."))
				: file.name;
			handleCustomFileNameChange(defaultFileName);
		}

		return false;
	};

	const handleRemoveQueuedFile = (fileToRemove: File) => {
		setPendingFiles((currentFiles) => currentFiles.filter((file) => !(
			file.name === fileToRemove.name &&
			file.size === fileToRemove.size &&
			file.lastModified === fileToRemove.lastModified
		)));

		if (!allowMultipleDocuments) {
			handleCustomFileNameChange("");
		}
	};

	const handleUploadQueuedFiles = async () => {
		const hasUploaded = await handleUploadPendingFiles(pendingFiles, shouldRunOcr);
		if (hasUploaded) {
			setPendingFiles([]);
		}
	};

	const handleMainButtonClick = async () => {
		if (
			(isSingleFileCustomNameMode || allowMultipleDocuments) &&
			pendingFiles.length > 0
		) {
			await handleUploadQueuedFiles();
		}
	};

	const mainButtonLabel = fileUrl
		? translations.changeFile
		: allowMultipleDocuments && pendingFiles.length === 0
			? (translations.selectFiles ?? 'Select Files')
			: translations.uploadDocument;
	const handleSelectFile = (file: File) => shouldQueueFilesBeforeUpload
		? handleQueueFile(file)
		: handleBeforeUpload(file, shouldRunOcr);
	const acceptedFormats = `.pdf,.jpg,.jpeg,.png${extraFormatsAllowed ? `,${extraFormatsAllowed}` : ''}`;

	return {
		acceptedFormats,
		handleSelectFile,
		mainButtonLabel,
		canUploadPendingFiles,
		displayCustomFileName,
		fileUrl,
		handleBeforeUpload,
		handleCustomFileNameChange,
		handleMainButtonClick,
		handleQueueFile,
		handleRemoveQueuedFile,
		handleUploadQueuedFiles,
		isOcrLoading,
		pendingFiles,
		shouldDisableFileDialog,
		shouldQueueFilesBeforeUpload,
		shouldShowCustomFileNameInput
	};
};

export default useUploadDocument;
